"use client";

import { useEffect, useState } from "react";
import { CheckSquare, Calendar, User, Flag, Link2, RotateCcw, Edit, Trash2, Save, FileText, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { post, put, del, can, getStoredUser } from "@/utils/service";
import { apiMessage, fieldErrors, label } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, DetailRow, darkInput, darkLabel, darkPrimary, darkSecondary, darkDanger } from "@/components/crm/FloatingDrawer";
import { useCounts, countText } from "@/components/crm/useCounts";
import ListShell, { addButton, pill } from "@/components/crm/ListShell";
import { useRemoteList } from "@/components/crm/useRemoteList";
import { useMemberOptions } from "@/components/crm/members";
import Kanban from "@/components/crm/Kanban";
import { useStatusBoard } from "@/components/crm/useStatusBoard";
import ViewToggle, { useListView } from "@/components/crm/ViewToggle";
import { toast } from "sonner";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";
import DateInput from "@/components/ui/date-input";

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_at: string | null;
  remind_at: string | null;
  completed_at: string | null;
  is_overdue: boolean;
  recurrence: string | null;
  related?: { type: string; id: string; name: string } | null;
  owner_id: string | null;
  owner?: { id: string; name: string } | null;
  can?: { update: boolean; delete: boolean } | null;
}

const VIEWS = [
  { id: "open", label: "Open" },
  { id: "today", label: "Due today" },
  { id: "overdue", label: "Overdue" },
  { id: "upcoming", label: "Upcoming" },
  { id: "completed", label: "Completed" },
];
const PRIORITY: Record<string, string> = { urgent: "bg-red-100 text-red-800", high: "bg-orange-100 text-orange-800", normal: "bg-gray-100 text-gray-700", low: "bg-gray-50 text-gray-500" };

const toLocal = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const BOARD_STATUSES = [
  { id: "open", title: "To do", dot: "bg-gray-400" },
  { id: "in_progress", title: "In progress", dot: "bg-blue-500" },
  { id: "completed", title: "Done", dot: "bg-green-500" },
  { id: "cancelled", title: "Cancelled", dot: "bg-red-400" },
];

const GROUPS = ["Overdue", "Today", "Tomorrow", "This week", "Later", "No due date", "Completed"] as const;
const groupOf = (t: Task): (typeof GROUPS)[number] => {
  if (t.status === "completed") return "Completed";
  if (!t.due_at) return "No due date";
  if (t.is_overdue) return "Overdue";
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const days = Math.floor((new Date(t.due_at).getTime() - start.getTime()) / 86_400_000);
  return days <= 0 ? "Today" : days === 1 ? "Tomorrow" : days < 7 ? "This week" : "Later";
};
const GROUP_TONE: Record<string, string> = { Overdue: "text-red-600", Today: "text-primary" };
const time = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "No due date");

function TaskForm({ isOpen, onClose, onSaved, task }: { isOpen: boolean; onClose: () => void; onSaved: () => void; task: Task | null }) {
  const members = useMemberOptions();
  const canAssign = can("tasks.manage_all");
  const blank = { title: "", description: "", priority: "normal", due_at: "", remind_at: "", recurrence: "", owner_id: "" };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setForm(task ? {
      title: task.title, description: task.description ?? "", priority: task.priority, due_at: toLocal(task.due_at), remind_at: toLocal(task.remind_at),
      recurrence: task.recurrence ?? "", owner_id: task.owner_id ?? "",
    } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, task]);

  const set = (k: keyof typeof blank, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setErrors({});
    const body: Record<string, unknown> = {
      title: form.title, description: form.description || null, priority: form.priority,
      due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
      remind_at: form.remind_at ? new Date(form.remind_at).toISOString() : null,
      recurrence: form.recurrence || null,
    };
    if (canAssign && form.owner_id) body.owner_id = form.owner_id;
    try {
      if (task) await put(`/tasks/${task.id}`, body);
      else await post("/tasks", body);
      notify(task ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not save the task."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader title={task ? "Edit Task" : "New Task"} subtitle="Something to do, with a due time and an optional reminder" />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div><label className={darkLabel}>Title *</label><input className={darkInput} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Call back about the proposal" />{errors.title && <p className="mt-1 text-xs text-red-400">{errors.title}</p>}</div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>Due</label><DateInput withTime className={darkInput} value={form.due_at} onChange={(e) => set("due_at", e.target.value)} /></div>
          <div><label className={darkLabel}>Remind me</label><DateInput withTime className={darkInput} value={form.remind_at} onChange={(e) => set("remind_at", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={darkLabel}>Priority</label>
            <AppSelect className={darkInput} value={form.priority} onChange={(e) => set("priority", e.target.value)}>
              {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{label(p)}</option>)}
            </AppSelect>
          </div>
          <div>
            <label className={darkLabel}>Repeats</label>
            <AppSelect className={darkInput} value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)}>
              <option value="">Does not repeat</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
            </AppSelect>
          </div>
        </div>
        {canAssign && (
          <div>
            <label className={darkLabel}>Assigned to</label>
            <AppSelect className={darkInput} value={form.owner_id} onChange={(e) => set("owner_id", e.target.value)}>
              <option value="">Me</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </AppSelect>
          </div>
        )}
        <div><label className={darkLabel}>Details</label><textarea rows={4} className={`${darkInput} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
      </div>
      <div className="p-6 border-t border-gray-800 space-y-3">
        <button onClick={submit} disabled={busy || !form.title} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : task ? "Update Task" : "Create Task"}</button>
        <button onClick={onClose} className={darkSecondary}>Cancel</button>
      </div>
    </FloatingDrawer>
  );
}

export default function TasksPage() {
  const me = getStoredUser();
  const [view, setView] = useState("open");
  const [mine, setMine] = useState(true);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const list = useRemoteList<Task>("/tasks", { view, search: query || undefined, owner: mine ? "me" : undefined, sort: view === "completed" ? "-completed_at" : "due_at" }, 20);
  const [layout, setLayout] = useListView("tasks", ["board", "list"] as const, "list");
  const { counts, loading: countsLoading } = useCounts("/tasks", {
    open: { view: "open", owner: mine ? "me" : undefined }, today: { view: "today", owner: mine ? "me" : undefined },
    overdue: { view: "overdue", owner: mine ? "me" : undefined }, done: { view: "completed", owner: mine ? "me" : undefined },
  }, list.rows.length);
  const [boardKey, setBoardKey] = useState(0);
  const board = useStatusBoard<Task>("/tasks-board", BOARD_STATUSES, { search: query || undefined, owner: mine ? "me" : undefined, sort: "due_at" }, boardKey, layout === "board");
  const reloadAll = () => { list.reload(); setBoardKey((k) => k + 1); };
  const moveTask = async (t: Task, from: string, to: string) => {
    try {
      if (to === "completed") {
        await post(`/tasks/${t.id}/complete`, {});
      } else {
        if (from === "completed") await post(`/tasks/${t.id}/reopen`, {});
        if (to !== "open" || from !== "completed") await put(`/tasks/${t.id}`, { status: to });
      }
      toast.success(`"${t.title}" moved to ${BOARD_STATUSES.find((b) => b.id === to)?.title ?? to}`);
      list.reload();
      if (to === "completed" && t.recurrence) setBoardKey((k) => k + 1); // the next occurrence appears in To do
    } catch (e) {
      notify(apiMessage(e, "Could not move the task."));
      return false;
    }
  };
  const [selected, setSelected] = useState<Task | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);

  const toggleDone = async (t: Task) => {
    try {
      await post(`/tasks/${t.id}/${t.status === "completed" ? "reopen" : "complete"}`, {});
      toast.success(t.status === "completed" ? "Task reopened" : t.recurrence ? "Done. The next one is scheduled." : "Task completed");
      setSelected(null);
      reloadAll();
    } catch (e) {
      notify(apiMessage(e, "Could not update the task."));
    }
  };

  const remove = async (t: Task) => {
    if (!await confirmDialog(`Delete "${t.title}"?`)) return;
    try {
      await del(`/tasks/${t.id}`);
      toast.success("Task deleted");
      setSelected(null);
      reloadAll();
    } catch (e) {
      notify(apiMessage(e, "Could not delete the task."));
    }
  };

  const grouped = (rows: Task[]) => (
    <div className="divide-y divide-gray-100">
      {GROUPS.map((g) => {
        const items = rows.filter((t) => groupOf(t) === g);
        if (!items.length) return null;
        return (
          <section key={g} className="px-4 py-3">
            <h3 className={`mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${GROUP_TONE[g] ?? "text-gray-500"}`}>
              {g}<span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-gray-600">{items.length}</span>
            </h3>
            <ul className="space-y-2">
              {items.map((t) => {
                const done = t.status === "completed";
                return (
                  <li key={t.id} onClick={() => setSelected(t)} className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 transition hover:border-primary/40 hover:shadow-sm">
                    <button
                      aria-label={done ? "Reopen" : "Mark as done"}
                      disabled={t.can?.update === false}
                      onClick={(e) => { e.stopPropagation(); toggleDone(t); }}
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${done ? "border-primary bg-primary text-white" : "border-gray-300 hover:border-primary"}`}
                    >
                      {done && <CheckCircle2 className="h-3.5 w-3.5" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${done ? "text-gray-400 line-through" : "font-medium text-gray-900"}`}>{t.title}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                        {t.due_at && <span className={`inline-flex items-center gap-1 ${t.is_overdue && !done ? "font-semibold text-red-600" : ""}`}><Calendar className="h-3 w-3" />{time(t.due_at)}</span>}
                        {t.related && <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{t.related.name}</span>}
                        {t.recurrence && <span className="inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" />{label(t.recurrence)}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      {t.priority !== "normal" && <span className={pill(PRIORITY[t.priority] ?? PRIORITY.normal)}>{label(t.priority)}</span>}
                      <span className="text-[11px] text-gray-500">{t.owner_id === me?.id ? "Me" : t.owner?.name ?? "Unassigned"}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Tasks" description="Follow-ups and to-dos, with reminders">
          <ViewToggle view={layout} onChange={setLayout} options={[{ id: "board", label: "Board" }, { id: "list", label: "List" }]} />
          {layout === "board" && can("tasks.create") && <button onClick={() => { setEditing(null); setFormOpen(true); }} className={addButton}>New Task</button>}
        </PageHeader>

        {layout === "board" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput className="w-full sm:w-72" placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} />
              {can("tasks.view_all") && (
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" className="accent-primary" checked={mine} onChange={(e) => setMine(e.target.checked)} />
                  Only my tasks
                </label>
              )}
            </div>
            <Kanban
              columns={board.columns}
              loading={board.loading}
              noun="Task"
              emptyText="No tasks"
              getId={(t) => t.id}
              onOpen={setSelected}
              canMove={(t) => t.can?.update !== false}
              onMove={moveTask}
              renderCard={(t) => (
                <div className="pr-6">
                  <p className={`text-sm ${t.status === "completed" ? "text-gray-400 line-through" : "font-medium text-gray-900"}`}>{t.title}</p>
                  {t.related && <p className="mt-0.5 truncate text-xs text-gray-500">{t.related.name}</p>}
                  <div className="-mr-6 mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-[11px] text-gray-500">
                    <span className={`inline-flex items-center gap-1 ${t.is_overdue && t.status !== "completed" ? "font-semibold text-red-600" : ""}`}><Calendar className="h-3 w-3" />{t.due_at ? time(t.due_at) : "No due date"}</span>
                    <span className="flex items-center gap-1.5">
                      {t.priority !== "normal" && <span className={pill(PRIORITY[t.priority] ?? PRIORITY.normal)}>{label(t.priority)}</span>}
                      <span>{t.owner_id === me?.id ? "Me" : t.owner?.name ?? ""}</span>
                    </span>
                  </div>
                </div>
              )}
            />
          </div>
        ) : (
        <ListShell
          renderBody={grouped}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          onPerPage={list.setPerPage}
          noun="tasks"
          onRowClick={setSelected}
          onAdd={can("tasks.create") ? () => { setEditing(null); setFormOpen(true); } : undefined}
          addLabel="New Task"
          grid={{
            id: "tasks",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            view: (
              <div className="flex max-w-full overflow-x-auto rounded-full bg-gray-100 p-0.5">
                {VIEWS.map((v) => (
                  <button key={v.id} onClick={() => setView(v.id)} className={`shrink-0 rounded-full px-3 py-1 text-xs ${view === v.id ? "bg-white font-semibold text-gray-900 shadow-sm" : "text-gray-600"}`}>{v.label}</button>
                ))}
              </div>
            ),
            filters: can("tasks.view_all") ? (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" className="accent-primary" checked={mine} onChange={(e) => setMine(e.target.checked)} />
                Only my tasks
              </label>
            ) : undefined,
            activeFilters: mine ? 1 : 0,
            onClearFilters: () => setMine(false),
            stats: [
              { label: "Open", value: countText(counts.open) },
              { label: "Due today", value: countText(counts.today), tone: counts.today ? "warn" : "default" },
              { label: "Overdue", value: countText(counts.overdue), tone: counts.overdue ? "bad" : "default" },
              { label: "Completed", value: countText(counts.done), tone: "good" },
            ],
            statsLoading: countsLoading,
          }}
          empty={{ icon: CheckSquare, title: view === "completed" ? "Nothing completed yet" : "You're all clear", text: view === "open" ? "No open tasks. Add one, or schedule a next step from a lead." : "No tasks in this view." }}
        />
        )}
      </div>

      <FloatingDrawer isOpen={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <>
            <DrawerHeader title="Task" subtitle={selected.title} />
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <DetailRow icon={Calendar} label="Due"><span className={selected.is_overdue ? "text-red-400" : ""}>{when(selected.due_at)}</span>{selected.recurrence && ` · repeats ${selected.recurrence}`}</DetailRow>
              <DetailRow icon={Flag} label="Priority">{label(selected.priority)}</DetailRow>
              <DetailRow icon={User} label="Assigned to">{selected.owner?.name ?? "—"}</DetailRow>
              {selected.related && <DetailRow icon={Link2} label={`Related ${selected.related.type}`}>{selected.related.name}</DetailRow>}
              {selected.completed_at && <DetailRow icon={CheckCircle2} label="Completed">{when(selected.completed_at)}</DetailRow>}
              {selected.description && <DetailRow icon={FileText} label="Details"><span className="whitespace-pre-line">{selected.description}</span></DetailRow>}
            </div>
            <div className="p-3 border-t border-gray-800 space-y-2">
              <button disabled={selected.can?.update === false} onClick={() => toggleDone(selected)} className={darkPrimary}>
                {selected.status === "completed" ? <><RotateCcw className="w-4 h-4" />Reopen</> : <><CheckCircle2 className="w-4 h-4" />Mark as done</>}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button disabled={selected.can?.update === false} onClick={() => { setEditing(selected); setSelected(null); setFormOpen(true); }} className={darkSecondary}><Edit className="inline w-4 h-4 mr-1" />Edit</button>
                <button disabled={selected.can?.delete === false} onClick={() => remove(selected)} className={darkDanger}><Trash2 className="w-4 h-4" />Delete</button>
              </div>
            </div>
          </>
        )}
      </FloatingDrawer>

      <TaskForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSaved={reloadAll} task={editing} />
    </div>
  );
}
